  import React, { useState, useEffect, useCallback } from 'react';
  import PropTypes from 'prop-types';
  import { Table } from 'react-bootstrap';
  import moment from 'moment';

  const CORES = {
    DISPONIVEL: '#90EE90',
    EM_ATENDIMENTO: '#65D1C2',
    ENCAMINHADA: '#fff3cd',
    INATIVO: '#f8d7da',
    NAO_ESPERANDO: '#666666',
  };

  const COLUNAS = [
    { key: 'numero', label: 'Número', center: true },
    { key: 'nome_colaborador', label: 'Colaborador', center: false },
    { key: 'ultimo_atendimento', label: 'Último Atend.', center: true },
  ];

  const MesaGrid = ({ mesas }) => {
    const [sortConfig, setSortConfig] = useState(null);

    // Reset para ordem padrão sempre que os dados mudam (refresh/auto-refresh)
    useEffect(() => {
      setSortConfig(null);
    }, [mesas]);

    const formatHora = data => {
      if (!data) return '-';
      return moment(data).format('HH:mm:ss');
    };

    const getRowStyle = mesa => {
      if (!mesa.is_ativo) {
        return { backgroundColor: CORES.INATIVO };
      }
      if (mesa.em_atendimento) {
        return { backgroundColor: CORES.EM_ATENDIMENTO };
      }
      if (mesa.encaminhada) {
        return { backgroundColor: CORES.ENCAMINHADA };
      }
      if (!mesa.esperando_senha) {
        return { backgroundColor: CORES.NAO_ESPERANDO, color: '#fff' };
      }
      return { backgroundColor: CORES.DISPONIVEL };
    };

    const ordenarPadrao = useCallback((a, b) => {
      // Padrão: mesas sem atendimento do dia primeiro (por número),
      // mesas com atendimento do dia depois (por horário do atendimento, depois número)
      const aTemAtend = !!a.ultimo_atendimento;
      const bTemAtend = !!b.ultimo_atendimento;

      if (aTemAtend !== bTemAtend) {
        return aTemAtend ? 1 : -1;
      }

      if (aTemAtend && bTemAtend) {
        const ta = new Date(a.ultimo_atendimento).getTime();
        const tb = new Date(b.ultimo_atendimento).getTime();
        if (ta !== tb) return ta - tb;
      }

      return (a.numero || 0) - (b.numero || 0);
    }, []);

    const ordenarPorColuna = useCallback((a, b) => {
      if (!sortConfig) return 0;
      const { key, direction } = sortConfig;
      const mult = direction === 'asc' ? 1 : -1;

      let va = a[key];
      let vb = b[key];

      if (key === 'ultimo_atendimento') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return (va - vb) * mult;
      }

      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * mult;
      }

      va = (va || '').toString().toLowerCase();
      vb = (vb || '').toString().toLowerCase();
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    }, [sortConfig]);

    const handleSort = key => {
      setSortConfig(prev => {
        if (prev && prev.key === key) {
          if (prev.direction === 'asc') return { key, direction: 'desc' };
          // Terceiro clique volta ao padrão
          return null;
        }
        return { key, direction: 'asc' };
      });
    };

    const getSortIcon = key => {
      if (!sortConfig || sortConfig.key !== key) return ' ⇅';
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    if (!mesas || mesas.length === 0) {
      return (
        <div className="text-center text-muted py-4">
          Não há mesas cadastradas.
        </div>
      );
    }

    const mesasOrdenadas = [...mesas].sort(sortConfig ? ordenarPorColuna : ordenarPadrao);

    return (
      <div style={{ borderRadius: '8px', maxHeight: '400px', overflow: 'auto' }}>
        <Table bordered hover size="sm" className="mb-0">
          <thead style={{ backgroundColor: '#0747a6', color: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              {COLUNAS.map(col => (
                <th
                  key={col.key}
                  className={col.center ? 'text-center' : ''}
                  style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}{getSortIcon(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mesasOrdenadas.map(mesa => (
              <tr key={mesa.codigo} style={getRowStyle(mesa)}>
                <td className="text-center font-weight-bold">{mesa.numero}</td>
                <td>{mesa.nome_colaborador}</td>
                <td className="text-center">{formatHora(mesa.ultimo_atendimento)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  MesaGrid.propTypes = {
    mesas: PropTypes.arrayOf(
      PropTypes.shape({
        codigo: PropTypes.number.isRequired,
        numero: PropTypes.number.isRequired,
        nome_colaborador: PropTypes.string,
        em_atendimento: PropTypes.bool,
        encaminhada: PropTypes.bool,
        is_ativo: PropTypes.bool,
        esperando_senha: PropTypes.bool,
        ultimo_atendimento: PropTypes.string,
      })
    ),
  };

  MesaGrid.defaultProps = {
    mesas: [],
  };

  export default MesaGrid;
